import React, { useRef } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { createCustomWithErrorBoundaryForShellMenu } from '@components/ErrorBoundary/withErrorBoundary';
import * as Ids from '@utils/ids';
import MenuItem from './MenuItem';
import { RootState } from '../../store';
import { menuMatch } from '../../utils/menuMatch';
import createMenuObserver from './utils/menuObserver';
import { IQueryMenuItem, MatchedMenuType, IMenuInfo } from '../../api/define';
import styles from './LeftMenu.module.scss';

type LeftMenuProps = {
    /** 一级菜单是否展开 */
    isMenuFolded: boolean;
    /** 所有菜单列表 */
    leftMenuList: IQueryMenuItem[];
    /** 高亮匹配 */
    menuInfo: IMenuInfo;
    /** 当前主页面url */
    pageHash: string;
    /** hover临时展开 */
    hoverMenuUnFold: boolean;
    /** 设置菜单展开&关闭 */
    setMenuFold: (val: boolean) => void;
    /** 菜单初始化 */
    initPageData: () => void;
    /** 更新商品菜单tag数 （postmessage） */
    setLowQualitySpusCount: (val: number) => void;
    /** 更新高亮菜单 */
    setMatchedMenu: (val: MatchedMenuType) => void;
    /** hover展开菜单 */
    setHoverMenuUnFold: (val: boolean) => void;
};

const { useEffect, useCallback } = React;
/** 左侧导航栏菜单 */
const LeftMenu: React.FC<LeftMenuProps> = ({
    isMenuFolded,
    leftMenuList,
    menuInfo,
    pageHash,
    hoverMenuUnFold,
    setMenuFold,
    initPageData,
    setLowQualitySpusCount,
    setMatchedMenu,
    setHoverMenuUnFold,
}) => {
    //菜单外层元素
    const menusOutRef = useRef<HTMLDivElement>(null);

    //菜单refs用于存储菜单列表的引用
    const menusRef = useRef<Array<HTMLDivElement>>([]);

    useEffect(() => {
        initPageData();
    }, []);

    /**
     * 增加元素监听
     */
    useEffect(() => {
        let observerInstance: IntersectionObserver | null = null;
        if (Array.isArray(leftMenuList) && leftMenuList.length > 0 && menusOutRef.current) {
            observerInstance = createMenuObserver({
                root: menusOutRef.current,
                threshold: 0.5,
            });
            for (let i = 0; i < leftMenuList.length; i++) {
                if (menusRef.current[i] !== null) {
                    observerInstance.observe(menusRef.current[i]);
                }
            }
        }
        return () => {
            //停止监听 并销毁监听实例
            if (observerInstance) {
                observerInstance.disconnect();
                observerInstance = null;
            }
        };
    }, [leftMenuList]);

    // 根据hash变化，更新高亮菜单
    useEffect(() => {
        if (menuInfo && leftMenuList.length) {
            setMatchedMenu(menuMatch(pageHash, menuInfo, leftMenuList));
        }
    }, [pageHash, menuInfo, leftMenuList]);

    /** 商品相关tag */
    useEffect(() => {
        const postMessageHandler = (e: MessageEvent) => {
            let data = null;
            try {
                data = JSON.parse(e.data) || {};
            } catch (err) {
                // 🤫
            }
            if (data?.method === 'postMsg') {
                const args = data.args || [];
                const obj = args[0] || {};
                if (obj.type === 'productAssistant') {
                    const number = obj.number;
                    if (number > 0) {
                        setLowQualitySpusCount(number);
                    } else {
                        setLowQualitySpusCount(0);
                    }
                }
            }
        };
        window.addEventListener('message', postMessageHandler);
        return () => window.removeEventListener('message', postMessageHandler);
    }, [setLowQualitySpusCount]);

    /** hover展开菜单 */
    const mounseMoveEnter = useCallback(() => {
        if (isMenuFolded) setHoverMenuUnFold(true);
    }, [setHoverMenuUnFold, isMenuFolded]);

    const mounseMoveLeave = useCallback(() => {
        setHoverMenuUnFold(false);
    }, [setHoverMenuUnFold]);
    /** hover展开菜单 end */

    return (
        <>
            <div
                className={`${styles.menuContainer} ${
                    isMenuFolded && !hoverMenuUnFold ? styles.folded : ''
                } left-menu`}
                onMouseEnter={mounseMoveEnter}
                onMouseLeave={mounseMoveLeave}
            >
                {/* 菜单列表 */}
                <div className={styles.menus} ref={menusOutRef}>
                    <ul>
                        {leftMenuList.map((item, index) => (
                            <div
                                ref={el => {
                                    if (el) {
                                        menusRef.current[index] = el;
                                    }
                                }}
                                id={item.menuName}
                                key={item.menuName}
                            >
                                <MenuItem menu={item} />
                            </div>
                        ))}
                    </ul>
                </div>
                {/* 收起模块 */}
                <div
                    className={styles.sidebarToggle}
                    onClick={() => {
                        setHoverMenuUnFold(false);
                        setMenuFold(!isMenuFolded);
                    }}
                >
                    <svg className={`${styles.svgIcon} ${isMenuFolded ? styles.folded : ''}`} />
                    <span className={styles.text}>{isMenuFolded ? '向右展开' : '向左收起'}</span>
                </div>
            </div>
            {/* 低分屏下，展示全局遮罩 */}
            <div className={styles.fullScreenMask} />
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        leftMenuList: state.shellLeftMenu.leftMenuList,
        isMenuFolded: state.shellLeftMenu.isMenuFolded,
        menuInfo: state.shellLeftMenu.menuInfo,
        hoverMenuUnFold: state.shellLeftMenu.hoverMenuUnFold,
        pageHash: state.mainContent.pageHash,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    initPageData: () => {
        dispatch({ type: 'shellLeftMenu/getLeftMenuInfo' });
        if (Ids.getWmPoiId() !== '-1') {
            dispatch({ type: 'shellLeftMenu/getMenuAuth' });
            dispatch({ type: 'shellLeftMenu/getLowQualitySpusCount' });
        }
    },
    setMenuFold: (val: boolean) => {
        dispatch({ type: 'shellLeftMenu/setMenuFold', payload: val });
    },
    setHoverMenuUnFold: (val: boolean) => {
        dispatch({ type: 'shellLeftMenu/setHoverMenuUnFold', payload: val });
    },
    setMatchedMenu: (val: MatchedMenuType) => {
        dispatch({ type: 'shellLeftMenu/setMatchedMenu', payload: val });
    },
    setLowQualitySpusCount: (val: number) => {
        dispatch({ type: 'shellLeftMenu/setLowQualitySpusCount', payload: val });
    },
});

export default createCustomWithErrorBoundaryForShellMenu('菜单栏')(
    connect(mapStateToProps, mapDispatchToProps)(LeftMenu),
);
